
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SEOHead from '../../components/base/SEOHead';
import { useEffect } from 'react';

export default function Home() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com'}/#webpage`,
    "url": `${import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com'}`,
    "name": "Drixem Group - Malaysian Palm Oil Specialists | Premium Export & Consultation",
    "description": "Malaysia's premier palm oil specialists with 10+ years expertise. Government certified premium palm oil export, mill setup consultation, and OEM manufacturing services across 5 continents.",
    "isPartOf": {
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com'}/#website`
    },
    "about": {
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com'}/#organization`
    },
    "mainEntity": {
      "@type": "Service",
      "name": "Palm Oil Export Services",
      "description": "Premium Malaysian palm oil export services including CP6, CP8, CP10 grades",
      "provider": {
        "@id": `${import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com'}/#organization`
      }
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", ".hero-description", ".stats-section"]
    },
    "significantLink": [
      `${import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com'}/services`,
      `${import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com'}/products`,
      `${import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com'}/contact`
    ]
  };

  const services = [
    {
      icon: 'ri-building-line',
      title: 'Palm Oil Mill & Refinery Setup',
      description: 'Complete consultation and setup services for palm oil mills and refineries with cutting-edge technology.'
    },
    {
      icon: 'ri-ship-line',
      title: 'Palm Oil Exporting',
      description: 'Premium quality palm oil products including CP6, CP8, CP10 with flexible packaging options.'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'OEM Contract Manufacturing',
      description: 'Custom branding solutions for your palm oil products with our established manufacturing expertise.'
    }
  ];

  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '50+', label: 'Projects Completed' },
    { number: '5', label: 'Continents Served' },
    { number: '100%', label: 'Client Satisfaction' }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Malaysia's Leading Palm Oil Specialists | Premium Export & Manufacturing"
        description="Malaysia's premier palm oil specialists with 10+ years expertise. Government certified premium palm oil export (CP6, CP8, CP10), mill setup consultation, and OEM manufacturing services across 5 continents. JAKIM Halal & MPOB certified."
        keywords="Malaysian palm oil, RBD palm olein, CP6 CP8 CP10, JAKIM halal certified, MPOB licensed, palm oil export Malaysia, palm oil mill setup, OEM manufacturing Malaysia, Seremban palm oil, Negeri Sembilan, Malaysian government certified"
        structuredData={homeStructuredData}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/aecefb92fc7902afc5ee4ddae824379f.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Malaysia's Leading
              <span className="block text-yellow-400">Palm Oil Specialists</span>
            </h1>
            <p className="hero-description text-lg sm:text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed px-4">
              With over 10 years of expertise rooted in Malaysia, Drixem Group delivers comprehensive palm oil solutions, government-certified quality, and strategic partnerships across all five continents.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <Link to="/services" onClick={() => window.scrollTo(0, 0)}>
                <Button size="lg" className="text-base sm:text-lg w-full sm:w-auto">
                  <i className="ri-arrow-right-line mr-2"></i>
                  Explore Our Services
                </Button>
              </Link>
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>
                <Button variant="outline" size="lg" className="text-base sm:text-lg border-white text-white hover:bg-white hover:text-gray-900 w-full sm:w-auto">
                  <i className="ri-phone-line mr-2"></i>
                  Get Free Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-yellow-500 mb-2">{stat.number}</div>
                <div className="text-sm sm:text-base text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-yellow-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">Our Core Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive palm oil industry solutions designed to help you succeed in the global market
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-yellow-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                <Link to="/services" onClick={() => window.scrollTo(0, 0)} className="text-yellow-600 font-semibold hover:text-yellow-700 transition-colors">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6">Why Choose Drixem Group?</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Decade of Excellence</h4>
                    <p className="text-gray-600">Over 10 years of proven expertise in palm oil industry consultation and manufacturing.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Global Reach</h4>
                    <p className="text-gray-600">Successfully serving clients across all five continents with multi-currency support.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Premium Quality</h4>
                    <p className="text-gray-600">Halal-certified products and comprehensive food safety compliance.</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Link to="/about" onClick={() => window.scrollTo(0, 0)}>
                  <Button size="lg">Learn More About Us</Button>
                </Link>
              </div>
            </div>
            <div className="relative order-1 lg:order-2">
              <img 
                src="https://static.readdy.ai/image/5c4d3156b18a91d26c6b2b068b2e3a44/a385d34f901fc69a28716333c6568fac.jfif"
                alt="The Benefits of Palm Oil Infographic"
                className="rounded-2xl shadow-2xl object-contain w-full h-auto max-h-80 sm:max-h-96 bg-white p-2 sm:p-4"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-16 sm:py-20 bg-yellow-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6">Our Premium Products</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              High-quality palm oil products and OEM brands trusted by clients worldwide
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-product-shop>
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-48 sm:h-56 md:h-48 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/5c4d3156b18a91d26c6b2b068b2e3a44/b720338c0c656fa212a5785177202fb1.jfif"
                  alt="Premium Malaysian Palm Oil"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  width="400"
                  height="200"
                />
              </div>
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Premium Malaysian Palm Oil</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">CP6, CP8, CP10 grades from Malaysian plantations with government certifications</p>
                <span className="inline-block bg-yellow-100 text-yellow-600 px-3 py-1 rounded-full text-xs sm:text-sm font-medium">Malaysian Quality</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="h-48 sm:h-56 md:h-48 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/5c4d3156b18a91d26c6b2b068b2e3a44/0159a2ea39893b5b4f812e6bc31579e6.jfif"
                  alt="Malaysian OEM Products"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  width="400"
                  height="200"
                />
              </div>
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Malaysian OEM Manufacturing</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">Custom branded products manufactured in Malaysia with full compliance</p>
                <span className="inline-block bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs sm:text-sm font-medium">Made in Malaysia</span>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 md:col-span-2 lg:col-span-1">
              <div className="h-48 sm:h-56 md:h-48 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/5c4d3156b18a91d26c6b2b068b2e3a44/7f07017104b6a575a6ab1bb237c56bbd.jfif"
                  alt="Government Certified Products"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  width="400"
                  height="200"
                />
              </div>
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Government Certified</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">JAKIM Halal, MPOB, and Malaysian MOH certified for global markets</p>
                <span className="inline-block bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs sm:text-sm font-medium">Gov Certified</span>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-8 sm:mt-12">
            <Link to="/products" onClick={() => window.scrollTo(0, 0)}>
              <Button size="lg" className="w-full sm:w-auto">View All Products</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-yellow-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 px-4">Partner with Malaysia's Palm Oil Leaders</h2>
          <p className="text-lg sm:text-xl mb-8 max-w-3xl mx-auto px-4">
            Leverage our deep Malaysian roots, government certifications, and 10+ years of expertise to accelerate your success in the global palm oil industry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>
              <Button variant="secondary" size="lg" className="bg-white text-yellow-600 hover:bg-gray-100 w-full sm:w-auto">
                <i className="ri-phone-line mr-2"></i>
                Get Free Consultation
              </Button>
            </Link>
            <Link to="/services" onClick={() => window.scrollTo(0, 0)}>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-yellow-600 w-full sm:w-auto">
                <i className="ri-service-line mr-2"></i>
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
